export type ThemeType = 'light' | 'dark' | 'neon' | 'vibrant' | 'sunrise' | 'ocean' | 'forest';

export interface ThemeColors {
  primary: {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
  };
  secondary: {
    light: string;
    main: string;
    dark: string;
    contrastText: string;
  };
  accent: {
    coral: string;
    peach: string;
    mint: string;
    warmYellow: string;
    softPurple: string;
  };
  background: {
    default: string;
    paper: string;
    accent: string;
    gradient: string;
  };
  text: {
    primary: string;
    secondary: string;
    disabled: string;
  };
  border: {
    light: string;
    main: string;
  };
  success: {
    main: string;
    light: string;
  };
  error: {
    main: string;
    light: string;
  };
}

export const themes: Record<ThemeType, ThemeColors> = {
  light: {
    primary: {
      light: '#4B9EFF',
      main: '#2563EB',
      dark: '#1E40AF',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#9CA3AF',
      main: '#6B7280',
      dark: '#4B5563',
      contrastText: '#FFFFFF'
    },
    accent: {
      coral: '#E94560',
      peach: '#F97068',
      mint: '#4ECDC4',
      warmYellow: '#FFB84D',
      softPurple: '#9F7AEA'
    },
    background: {
      default: '#F3F4F6',
      paper: '#FFFFFF',
      accent: '#F9FAFB',
      gradient: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)'
    },
    text: {
      primary: '#111827',
      secondary: '#4B5563',
      disabled: '#9CA3AF'
    },
    border: {
      light: '#E5E7EB',
      main: '#D1D5DB'
    },
    success: {
      main: '#059669',
      light: '#ECFDF5'
    },
    error: {
      main: '#DC2626',
      light: '#FEE2E2'
    }
  },
  dark: {
    primary: {
      light: '#60A5FA',
      main: '#3B82F6',
      dark: '#2563EB',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#9CA3AF',
      main: '#6B7280',
      dark: '#4B5563',
      contrastText: '#FFFFFF'
    },
    accent: {
      coral: '#E94560',
      peach: '#F97068',
      mint: '#4ECDC4',
      warmYellow: '#FFB84D',
      softPurple: '#9F7AEA'
    },
    background: {
      default: '#1a1f2e',
      paper: '#242b3d',
      accent: '#2d3548',
      gradient: 'linear-gradient(135deg, #1a1f2e 0%, #242b3d 100%)'
    },
    text: {
      primary: '#F9FAFB',
      secondary: '#D1D5DB',
      disabled: '#6B7280'
    },
    border: {
      light: '#374151',
      main: '#4B5563'
    },
    success: {
      main: '#059669',
      light: '#064E3B'
    },
    error: {
      main: '#DC2626',
      light: '#7F1D1D'
    }
  },
  neon: {
    primary: {
      light: '#FF6EC7',
      main: '#FF006E',
      dark: '#C50055',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#73FFF7',
      main: '#00F5FF',
      dark: '#00C9D7',
      contrastText: '#000000'
    },
    accent: {
      coral: '#FF10F0',
      peach: '#FF6EC7',
      mint: '#00FFF0',
      warmYellow: '#FFFF00',
      softPurple: '#BF00FF'
    },
    background: {
      default: '#0A0A0F',
      paper: '#16161F',
      accent: '#1F1F2E',
      gradient: 'linear-gradient(135deg, #FF006E 0%, #00F5FF 50%, #BF00FF 100%)'
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#E0E0E0',
      disabled: '#808080'
    },
    border: {
      light: '#2A2A3E',
      main: '#3A3A4E'
    },
    success: {
      main: '#00FF88',
      light: '#00FF8840'
    },
    error: {
      main: '#FF0080',
      light: '#FF008040'
    }
  },
  vibrant: {
    primary: {
      light: '#8B5CF6',
      main: '#6D28D9',
      dark: '#5B21B6',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#EC4899',
      main: '#DB2777',
      dark: '#BE185D',
      contrastText: '#FFFFFF'
    },
    accent: {
      coral: '#E94560',
      peach: '#F97068',
      mint: '#4ECDC4',
      warmYellow: '#FFB84D',
      softPurple: '#9F7AEA'
    },
    background: {
      default: '#F5F3FF',
      paper: '#FFFFFF',
      accent: '#EEF2FF',
      gradient: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)'
    },
    text: {
      primary: '#1E1B4B',
      secondary: '#4338CA',
      disabled: '#6D28D9'
    },
    border: {
      light: '#E0E7FF',
      main: '#C7D2FE'
    },
    success: {
      main: '#059669',
      light: '#ECFDF5'
    },
    error: {
      main: '#DC2626',
      light: '#FEE2E2'
    }
  },
  sunrise: {
    primary: {
      light: '#FF8A80',
      main: '#FF6B6B',
      dark: '#E55555',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#FFD54F',
      main: '#FFD93D',
      dark: '#F9C74F',
      contrastText: '#000000'
    },
    accent: {
      coral: '#E94560',
      peach: '#F97068',
      mint: '#4ECDC4',
      warmYellow: '#FFB84D',
      softPurple: '#9F7AEA'
    },
    background: {
      default: '#FFF5F5',
      paper: '#FFFFFF',
      accent: '#FFF0E5',
      gradient: 'linear-gradient(135deg, #FF6B6B 0%, #FFD93D 100%)'
    },
    text: {
      primary: '#2D3436',
      secondary: '#636E72',
      disabled: '#B2BEC3'
    },
    border: {
      light: '#FFE0E0',
      main: '#FFB5B5'
    },
    success: {
      main: '#00B894',
      light: '#E3FCF7'
    },
    error: {
      main: '#D63031',
      light: '#FFECEC'
    }
  },
  ocean: {
    primary: {
      light: '#22D3EE',
      main: '#06B6D4',
      dark: '#0891B2',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#6EE7B7',
      main: '#4ECDC4',
      dark: '#2DD4BF',
      contrastText: '#FFFFFF'
    },
    accent: {
      coral: '#E94560',
      peach: '#F97068',
      mint: '#4ECDC4',
      warmYellow: '#FFB84D',
      softPurple: '#9F7AEA'
    },
    background: {
      default: '#F0F9FF',
      paper: '#FFFFFF',
      accent: '#E0F2FE',
      gradient: 'linear-gradient(135deg, #06B6D4 0%, #4ECDC4 100%)'
    },
    text: {
      primary: '#0C4A6E',
      secondary: '#0E7490',
      disabled: '#7DD3C0'
    },
    border: {
      light: '#BAE6FD',
      main: '#7DD3C0'
    },
    success: {
      main: '#059669',
      light: '#D1FAE5'
    },
    error: {
      main: '#DC2626',
      light: '#FEE2E2'
    }
  },
  forest: {
    primary: {
      light: '#34D399',
      main: '#10B981',
      dark: '#059669',
      contrastText: '#FFFFFF'
    },
    secondary: {
      light: '#A3E635',
      main: '#84CC16',
      dark: '#65A30D',
      contrastText: '#000000'
    },
    accent: {
      coral: '#E94560',
      peach: '#F97068',
      mint: '#4ECDC4',
      warmYellow: '#FFB84D',
      softPurple: '#9F7AEA'
    },
    background: {
      default: '#F0FDF4',
      paper: '#FFFFFF',
      accent: '#ECFCCB',
      gradient: 'linear-gradient(135deg, #10B981 0%, #84CC16 100%)'
    },
    text: {
      primary: '#14532D',
      secondary: '#166534',
      disabled: '#86EFAC'
    },
    border: {
      light: '#D1FAE5',
      main: '#A7F3D0'
    },
    success: {
      main: '#059669',
      light: '#D1FAE5'
    },
    error: {
      main: '#DC2626',
      light: '#FEE2E2'
    }
  }
}; 